import { StateCreator } from 'zustand';
import type { Modifier } from '../../../types/dice';
import { ModifierSource } from '../../../types/dice';
import { ActiveCombatState, getStateDefinition } from '../../../data/combatStateDefinitions';

export interface CombatStatesSlice {
  // State
  combatStates: Record<string, ActiveCombatState>;

  // Actions
  activateState: (stateId: string, options?: { roundsRemaining?: number; source?: string }) => void;
  deactivateState: (stateId: string) => void;
  toggleState: (stateId: string) => void;
  tickRound: () => void;
  clearAllStates: () => void;

  // Selectors
  getActiveStates: () => ActiveCombatState[];
  isStateActive: (stateId: string) => boolean;
  getStateModifiers: () => Modifier[];
}

export const createCombatStatesSlice: StateCreator<CombatStatesSlice> = (set, get) => ({
  combatStates: {},

  activateState: (stateId, options) => {
    const definition = getStateDefinition(stateId);
    if (!definition) {
      console.warn(`[CombatStates] Unknown state ID: "${stateId}"`);
      return;
    }

    // Talent gating: check character has required talent
    if (definition.requiresTalent) {
      // Access the full store to read character talents
      const fullState = get() as unknown as { character: { talents?: { id: string }[] } | null };
      const character = fullState.character;
      const hasTalent = character?.talents?.some(
        (t: { id: string }) => t.id === definition.requiresTalent
      );
      if (!hasTalent) {
        console.warn(
          `[CombatStates] Cannot activate "${stateId}": character lacks required talent "${definition.requiresTalent}"`
        );
        return;
      }
    }

    set((state) => ({
      combatStates: {
        ...state.combatStates,
        [stateId]: {
          id: stateId,
          active: true,
          roundsRemaining: options?.roundsRemaining ?? null,
          source: options?.source,
          activatedAt: Date.now()
        }
      }
    }));
  },

  deactivateState: (stateId) => {
    set((state) => {
      const { [stateId]: _, ...rest } = state.combatStates;
      return { combatStates: rest };
    });
  },

  toggleState: (stateId) => {
    const { combatStates } = get();
    if (combatStates[stateId]) {
      get().deactivateState(stateId);
    } else {
      get().activateState(stateId);
    }
  },

  tickRound: () => {
    set((state) => {
      const updated: Record<string, ActiveCombatState> = {};
      for (const [id, activeState] of Object.entries(state.combatStates)) {
        if (activeState.roundsRemaining === null) {
          // Indefinite — keep as-is
          updated[id] = activeState;
        } else if (activeState.roundsRemaining > 1) {
          // Decrement
          updated[id] = { ...activeState, roundsRemaining: activeState.roundsRemaining - 1 };
        }
        // roundsRemaining <= 1 after decrement means 0 → auto-deactivate (don't add to map)
      }
      return { combatStates: updated };
    });
  },

  clearAllStates: () => {
    set({ combatStates: {} });
  },

  getActiveStates: () => {
    return Object.values(get().combatStates);
  },

  isStateActive: (stateId) => {
    return !!get().combatStates[stateId];
  },

  getStateModifiers: () => {
    const { combatStates } = get();
    const activeIds = Object.keys(combatStates);
    const modifiers: Modifier[] = [];

    // Build a set of suppressed state IDs based on active states' `suppresses` arrays
    const suppressedIds = new Set<string>();
    for (const id of activeIds) {
      const def = getStateDefinition(id);
      if (def?.suppresses) {
        for (const suppressed of def.suppresses) {
          suppressedIds.add(suppressed);
        }
      }
    }

    for (const id of activeIds) {
      const definition = getStateDefinition(id);
      if (!definition || definition.modifiers.length === 0) continue;

      // Check if this state is suppressed by any active state
      // A state is suppressed if its id appears in another active state's `suppresses` array
      if (suppressedIds.has(id)) continue;

      // Also check the definition's own `suppressedBy` field
      const isSuppressedBy = definition.suppressedBy.some((suppressorId) =>
        activeIds.includes(suppressorId)
      );
      if (isSuppressedBy) continue;

      // Collect modifiers from this state
      for (const mod of definition.modifiers) {
        modifiers.push({
          name: mod.description,
          value: mod.value,
          source: ModifierSource.Situational,
          description: mod.description
        });
      }
    }

    return modifiers;
  }
});
